import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { STATUS_META, ARCHIVE_REASONS } from "@/lib/constants";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { MagnifyingGlass, PersonSimpleWalk, DownloadSimple, Archive } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ExportColumnsDialog } from "@/components/ExportColumnsDialog";

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-medium mt-0.5">{value || "—"}</p>
    </div>
  );
}

const ENDPOINTS = {
  retirees: "/retirees",
  approaching: "/retirees/approaching",
};

export default function Retirees() {
  const [tab, setTab] = useState("retirees");
  const [rows, setRows] = useState([]);
  const [orgs, setOrgs] = useState({ regions: [], chapters: [], facilities: [] });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");

  const rName = (id) => orgs.regions.find((r) => r.id === id)?.name || "—";
  const fName = (id) => orgs.facilities.find((f) => f.id === id)?.name || "—";
  const facility = (id) => orgs.facilities.find((f) => f.id === id) || {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab === "retirees" ? { search } : {};
      const { data } = await api.get(ENDPOINTS[tab], { params });
      setRows(data);
    } finally { setLoading(false); }
  }, [tab, search]);

  useEffect(() => { api.get("/orgs").then((r) => setOrgs(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const handleExport = async (pick) => {
    setExporting(true);
    try {
      const res = await api.get("/export/retirees", { responseType: "blob", params: pick });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `retirees_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Retirees export downloaded");
      setPickOpen(false);
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const confirmArchive = async () => {
    setBusy(true);
    try {
      await api.post(`/members/${selected.id}/archive`, { reason: archiveReason.trim() });
      toast.success("Member archived");
      setArchiveOpen(false);
      setArchiveReason("");
      setSelected(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="animate-fade-up">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">National oversight</span>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
              <PersonSimpleWalk size={30} weight="bold" className="text-primary" /> Retirees
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              {rows.length} {tab === "approaching" ? "member nearing retirement (58-59)" : "member at retirement age (60+)"}{rows.length !== 1 ? "s" : ""}.
            </p>
          </div>
          <Button onClick={() => setPickOpen(true)} disabled={exporting} variant="outline"
            data-testid="export-retirees-button" className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            <DownloadSimple size={18} weight="bold" /> {exporting ? "Exporting…" : "Export retirees"}
          </Button>
        </div>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); setSearch(""); }} className="mt-6">
          <TabsList>
            <TabsTrigger value="retirees" data-testid="tab-retirees">Retired (60+)</TabsTrigger>
            <TabsTrigger value="approaching" data-testid="tab-approaching">Approaching (58-59)</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === "retirees" && (
          <div className="relative mt-5 max-w-md">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="retiree-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, staff ID, membership ID…" className="pl-10" />
          </div>
        )}

        <Card className="mt-5 border-border overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Member</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Membership ID</TableHead>
                  <TableHead className="hidden md:table-cell">Facility</TableHead>
                  <TableHead className="hidden md:table-cell">Region</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Nothing here yet.</TableCell></TableRow>
                ) : rows.map((m) => {
                  const st = STATUS_META[m.application_status] || STATUS_META.incomplete;
                  const badgeCls = tab === "approaching" ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400" : "bg-primary/10 text-primary";
                  return (
                    <TableRow key={m.id} data-testid={`retiree-row-${m.staff_id}`}
                      className="cursor-pointer" onClick={() => setSelected(m)}>
                      <TableCell>
                        <p className="font-medium">{m.first_name} {m.last_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{m.staff_id}</p>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 font-semibold tabular-nums">
                          {m.age}
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${badgeCls}`}>{tab === "approaching" ? "Nearing" : "Retired"}</span>
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{m.membership_id || "—"}</TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{fName(m.facility_id)}</TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{rName(m.region_id)}</TableCell>
                      <TableCell><span className={`text-xs font-medium px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="retiree-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{selected.first_name} {selected.middle_name} {selected.last_name}</DialogTitle>
              </DialogHeader>
              <div className="flex items-center justify-between rounded-md bg-muted px-4 py-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Membership ID</p>
                  <p className="font-mono text-lg font-semibold tracking-wider">{selected.membership_id || "Not assigned"}</p>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary">
                  Age {selected.age}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <Field label="Staff ID" value={selected.staff_id} />
                <Field label="SSNIT Number" value={selected.ssnit_number} />
                <Field label="Sex" value={selected.sex} />
                <Field label="Date of Birth" value={selected.date_of_birth} />
                <Field label="Age" value={selected.age != null ? String(selected.age) : null} />
                <Field label="Professional Grade" value={selected.professional_grade} />
                <Field label="Contact Number" value={selected.contact_number} />
                <Field label="Alternate Contact" value={selected.alternate_contact_number} />
                <Field label="Email" value={selected.email} />
                <Field label="Next of Kin" value={selected.next_of_kin_name} />
                <Field label="Next of Kin Contact" value={selected.next_of_kin_contact} />
                <Field label="Relationship" value={selected.next_of_kin_relationship} />
                <Field label="Facility" value={facility(selected.facility_id).name} />
                <Field label="Type of Facility" value={facility(selected.facility_id).type} />
                <Field label="Location" value={facility(selected.facility_id).location} />
                <Field label="District" value={facility(selected.facility_id).district} />
                <Field label="Region" value={rName(selected.region_id)} />
                <Field label="Status" value={selected.application_status} />
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-border mt-2">
                <Button disabled={busy || (selected.role && selected.role !== "member")} onClick={() => setArchiveOpen(true)}
                  title={selected.role && selected.role !== "member" ? "Unassign this member's role before archiving" : undefined}
                  data-testid="archive-button" className="gap-2 bg-slate-700 hover:bg-slate-800 text-white">
                  <Archive size={18} weight="bold" /> Retire &amp; archive
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={archiveOpen} onOpenChange={(o) => !o && setArchiveOpen(false)}>
        <DialogContent className="max-w-md" data-testid="retiree-archive-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Archive member</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {selected && <>Archive <span className="font-semibold text-foreground">{selected.first_name} {selected.last_name}</span> ({selected.staff_id})? They move to the Archived Members list and can be restored later.</>}
          </p>
          <div className="space-y-1.5 pt-1">
            <Label htmlFor="retiree-archive-reason">Reason for archiving</Label>
            <div className="flex flex-wrap gap-1.5">
              {ARCHIVE_REASONS.map((r) => (
                <button key={r} type="button" data-testid={`retiree-archive-preset-${r}`}
                  onClick={() => setArchiveReason(r)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${archiveReason === r ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                  {r}
                </button>
              ))}
            </div>
            <Textarea id="retiree-archive-reason" data-testid="retiree-archive-reason" rows={3}
              value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="e.g. Retired at 60, statutory retirement…" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setArchiveOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={confirmArchive} disabled={busy} data-testid="confirm-retiree-archive-button"
              className="gap-2 bg-slate-700 hover:bg-slate-800 text-white">
              <Archive size={16} weight="bold" /> {busy ? "Archiving…" : "Archive"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <ExportColumnsDialog open={pickOpen} onOpenChange={setPickOpen} kind="retirees" onExport={handleExport} exporting={exporting} />
    </Layout>
  );
}
